import { supabase } from '@/lib/supabase';
import { getKoreanDate, getKoreanTomorrowDate } from '@/lib/dateUtils';
import { upsertProductStock, getProductPrice } from './seller-helpers';
import { subtractMinutes } from './discount/discount-time';
import { imageService } from '../image';
import { getTodayOperatingHours } from './seller-store';

/**
 * 판매자의 상품 목록을 가져옵니다
 */
export async function getSellerProducts(restaurantId: string): Promise<{
  success: boolean;
  data?: any[];
  error?: string;
}> {
  try {
    const today = getKoreanDate();
    const { data, error } = await supabase
      .from('products')
      .select(`
        id,
        name,
        description,
        original_price,
        category,
        image_url,
        is_available,
        stock_quantity,
        stock:product_stock(
          date,
          initial_stock,
          current_stock
        )
      `)
      .eq('restaurant_id', restaurantId)
      .eq('is_available', true)
      .eq('is_deadline_template', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Products fetch error:', error);
      throw error;
    }

    // stock_quantity 우선, 없으면 오늘자 stock.initial_stock → 마지막으로 0
    const normalized = (data || []).map((p: any) => {
      const todayStock = (p.stock || []).find((s: any) => s.date === today);
      return {
        ...p,
        stock: p.stock_quantity ?? todayStock?.initial_stock ?? 0,
      };
    });

    return {
      success: true,
      data: normalized
    };
  } catch (error) {
    console.error('Get seller products error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '상품 목록을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 마감할인 템플릿 상품 목록을 가져옵니다
 */
export async function getDeadlineTemplateProducts(restaurantId: string): Promise<{
  success: boolean;
  data?: any[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('products')
      .select(`
        id,
        name,
        description,
        original_price,
        stock_quantity,
        image_url,
        category,
        is_available
      `)
      .eq('restaurant_id', restaurantId)
      .eq('is_deadline_template', true);

    if (error) {
      console.error('Deadline template products fetch error:', error);
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Get deadline template products error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '마감할인 템플릿 상품을 불러오는데 실패했습니다'
    };
  }
}

/**
 * 마감할인 템플릿 상품을 upsert합니다
 */
export async function upsertDeadlineTemplateProducts(
  restaurantId: string,
  templates: Array<{
    id?: string;
    name: string;
    description?: string;
    price: number;
    stock: number;
    category?: string;
    isAvailable?: boolean;
  }>
): Promise<{ success: boolean; productIds?: string[]; error?: string }> {
  const productIds: string[] = [];

  try {
    for (const template of templates) {
      const category = template.category || '에코팩';
      const isAvailable = template.isAvailable ?? true;
      if (template.id) {
        const { error } = await supabase
          .from('products')
          .update({
            name: template.name,
            description: template.description || null,
            original_price: template.price,
            stock_quantity: template.stock,
            category,
            is_available: isAvailable,
            is_deadline_template: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', template.id);

        if (error) throw error;
        productIds.push(template.id);
      } else {
        const { data, error } = await supabase
          .from('products')
          .insert({
            restaurant_id: restaurantId,
            name: template.name,
            description: template.description || null,
            original_price: template.price,
            stock_quantity: template.stock,
            category,
            is_available: isAvailable,
            is_deadline_template: true
          })
          .select('id')
          .single();

        if (error) throw error;
        if (data?.id) {
          productIds.push(data.id);
        }
      }
    }

    return { success: true, productIds };
  } catch (error) {
    console.error('Upsert deadline template products error:', error);
    return {
      success: false,
      productIds: productIds.length > 0 ? productIds : undefined,
      error: error instanceof Error ? error.message : '마감할인 템플릿 저장에 실패했습니다'
    };
  }
}

/**
 * 마감할인 템플릿 상품을 삭제합니다
 */
export async function deleteDeadlineTemplateProduct(productId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId)
      .eq('is_deadline_template', true);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Delete deadline template product error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '템플릿 상품 삭제에 실패했습니다'
    };
  }
}

type DeadlineTemplateForSync = {
  id?: string;
  imageFile?: File | null;
  imageUrl?: string;
  category?: string;
};

/**
 * 마감할인 템플릿 이미지/카테고리 동기화
 * - 상품 등록 페이지와 동일한 업로드 파이프라인(imageService.uploadProductImage) 사용
 * - 이미지가 없으면 null로 저장 (UI fallback 사용)
 * - 카테고리는 기본적으로 '에코팩'으로 통일
 */
export async function syncDeadlineTemplateImages(params: {
  templates: DeadlineTemplateForSync[];
  productIds: string[];
}): Promise<{ success: boolean; error?: string }> {
  const { templates, productIds } = params;

  try {
    for (let i = 0; i < templates.length; i++) {
      const template = templates[i];
      const productId = productIds?.[i] || template.id;

      if (!productId) {
        return { success: false, error: '상품 ID 매핑에 실패했습니다' };
      }

      const category = template.category || '에코팩';
      let resolvedImageUrl = template.imageUrl;

      if (template.imageFile) {
        const uploadResult = await imageService.uploadProductImage(
          template.imageFile,
          productId
        );

        if (!uploadResult.success || !uploadResult.url) {
          return {
            success: false,
            error: uploadResult.error || '이미지 업로드에 실패했습니다'
          };
        }

        resolvedImageUrl = uploadResult.url;
      } else if (!resolvedImageUrl) {
        resolvedImageUrl = undefined;
      }

      const { error: updateError } = await supabase
        .from('products')
        .update({
          image_url: resolvedImageUrl,
          category
        })
        .eq('id', productId);

      if (updateError) {
        return {
          success: false,
          error: updateError.message || '상품 이미지 URL 업데이트에 실패했습니다'
        };
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Sync deadline template images error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '마감할인 템플릿 이미지 동기화에 실패했습니다'
    };
  }
}

/**
 * 마감할인 상품을 설정합니다
 */
export async function setDeadlineDiscountProducts(
  restaurantId: string,
  products: Array<{
    productId: string;
    discountRate: number;
    stock: number;
    pickupStartTime: string;
    pickupEndTime: string;
  }>,
  options?: {
    useCustomOrderWindow?: boolean;
    customOrderStartTime?: string;
    customOrderEndTime?: string;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const today = getKoreanDate();
    const useCustomOrderWindow = options?.useCustomOrderWindow === true;
    const customOrderStartTime = options?.customOrderStartTime;
    const customOrderEndTime = options?.customOrderEndTime;

    // 매장 설정 조회 (deadline_always_open 플래그)
    const { data: restaurantConfig, error: restaurantError } = await supabase
      .from('restaurants')
      .select('deadline_always_open')
      .eq('id', restaurantId)
      .single();

    if (restaurantError) {
      throw restaurantError;
    }
    const deadlineAlwaysOpen = restaurantConfig?.deadline_always_open === true;

    // 영업시간 조회 (restaurant_operating_hours 기반)
    const todayHours = await getTodayOperatingHours(restaurantId);
    const businessOpenTime = todayHours?.open_time || null;
    const businessCloseTime = todayHours?.close_time || null;
    const isClosedToday = todayHours?.is_closed === true;

    // 레스토랑 마감시간 업데이트용 최소/최대 저장
    let restaurantDeadlineStart: string | null = null;
    let restaurantDeadlineEnd: string | null = null;
    
    for (const product of products) {
      // 1. product_offers에 마감할인 생성/업데이트
      const discountedPrice = Math.floor(
        (await getProductPrice(product.productId)) * (100 - product.discountRate) / 100
      );

      // 주문 가능 시간: 기본은 수령시작 30분 전~수령종료, 플래그 매장은 영업시간 내로 확장
      let deadlineOrderStartTime = useCustomOrderWindow && customOrderStartTime
        ? customOrderStartTime
        : subtractMinutes(product.pickupStartTime, 30);
      let deadlineOrderEndTime = useCustomOrderWindow && customOrderEndTime
        ? customOrderEndTime
        : product.pickupEndTime;

      if (deadlineAlwaysOpen && !useCustomOrderWindow) {
        if (isClosedToday) {
          // 휴무일이면 주문창을 닫음
          deadlineOrderStartTime = '23:59';
          deadlineOrderEndTime = '23:59';
        } else {
          // 영업시간 정보가 있으면 영업시간으로 설정, 없으면 기존 계산 유지
          if (businessOpenTime) deadlineOrderStartTime = businessOpenTime;
          if (businessCloseTime) deadlineOrderEndTime = businessCloseTime;
        }
      }
      
      // 기존 마감할인 오퍼가 있는지 확인
      const { data: existingOffer, error: offerError } = await supabase
        .from('product_offers')
        .select('id')
        .eq('product_id', product.productId)
        .eq('offer_type', 'DEADLINE_DISCOUNT')
        .eq('is_current', true)
        .maybeSingle();
      
      // PGRST116: Row not found는 정상적인 경우이므로 무시
      if (offerError && offerError.code !== 'PGRST116') {
        console.error('Unexpected error checking existing offer:', offerError);
        throw offerError;
      }

      if (existingOffer) {
        // 기존 오퍼 업데이트 - valid_from도 당일로 갱신
        await supabase
          .from('product_offers')
          .update({
            discount_rate: product.discountRate,
            discounted_price: discountedPrice,
            deadline_pickup_start_time: product.pickupStartTime,
            deadline_pickup_end_time: product.pickupEndTime,
            deadline_order_start_time: deadlineOrderStartTime,
            deadline_order_end_time: deadlineOrderEndTime,
            valid_from: `${today}T${deadlineOrderStartTime}:00Z`,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingOffer.id);
      } else {
        // 새 오퍼 생성 - valid_from을 당일로 자동 설정
        await supabase
          .from('product_offers')
          .insert({
            product_id: product.productId,
            offer_type: 'DEADLINE_DISCOUNT',
            discount_rate: product.discountRate,
            discounted_price: discountedPrice,
            deadline_pickup_start_time: product.pickupStartTime,
            deadline_pickup_end_time: product.pickupEndTime,
            deadline_order_start_time: deadlineOrderStartTime,
            deadline_order_end_time: deadlineOrderEndTime,
            valid_from: `${today}T${deadlineOrderStartTime}:00Z`,
            is_active: true,
            is_current: true
          });
      }

      // 2. product_stock에 당일 재고 설정
      await upsertProductStock(product.productId, today, product.stock);

      // 레스토랑 마감시간 추적: always_open이면 영업시간 전체로 확장
      const startCandidate = useCustomOrderWindow
        ? (customOrderStartTime || product.pickupStartTime)
        : (deadlineAlwaysOpen
            ? (isClosedToday ? '23:59' : businessOpenTime || product.pickupStartTime)
            : product.pickupStartTime);
      const endCandidate = useCustomOrderWindow
        ? (customOrderEndTime || product.pickupEndTime)
        : (deadlineAlwaysOpen
            ? (isClosedToday ? '23:59' : businessCloseTime || product.pickupEndTime)
            : product.pickupEndTime);

      // earliest/ latest 계산
      if (startCandidate) {
        if (!restaurantDeadlineStart || startCandidate < restaurantDeadlineStart) {
          restaurantDeadlineStart = startCandidate;
        }
      }
      if (endCandidate) {
        if (!restaurantDeadlineEnd || endCandidate > restaurantDeadlineEnd) {
          restaurantDeadlineEnd = endCandidate;
        }
      }
    }

    // 3. 레스토랑 테이블 마감시간 동기화 (always_open 시 영업시간 전체 반영)
    if (restaurantDeadlineStart && restaurantDeadlineEnd) {
      await supabase
        .from('restaurants')
        .update({
          deadline_discount_start_time: restaurantDeadlineStart,
          deadline_discount_end_time: restaurantDeadlineEnd,
          use_custom_deadline_order_window: useCustomOrderWindow,
          custom_deadline_order_start_time: useCustomOrderWindow ? (customOrderStartTime ?? restaurantDeadlineStart) : null,
          custom_deadline_order_end_time: useCustomOrderWindow ? (customOrderEndTime ?? restaurantDeadlineEnd) : null,
          timeout_exempt: useCustomOrderWindow ? true : false
        })
        .eq('id', restaurantId);
    }

    return { success: true };
  } catch (error) {
    console.error('Set deadline discount products error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '마감할인 상품 설정에 실패했습니다'
    };
  }
}

/**
 * 예약할인 상품을 설정합니다
 */
export async function setReservationDiscountProducts(
  _restaurantId: string,
  products: Array<{
    productId: string;
    discountRate: number;
    stock: number;
  }>
): Promise<{ success: boolean; error?: string }> {
  try {
    const tomorrowStr = getKoreanTomorrowDate();
    
    for (const product of products) {
      // 1. product_offers에 예약할인 생성/업데이트
      const discountedPrice = Math.floor(
        (await getProductPrice(product.productId)) * (100 - product.discountRate) / 100
      );
      
      // 기존 예약할인 오퍼가 있는지 확인
      const { data: existingOffer, error: offerError } = await supabase
        .from('product_offers')
        .select('id')
        .eq('product_id', product.productId)
        .eq('offer_type', 'RESERVATION_DISCOUNT')
        .eq('is_current', true)
        .maybeSingle();
      
      // PGRST116: Row not found는 정상적인 경우이므로 무시
      if (offerError && offerError.code !== 'PGRST116') {
        console.error('Unexpected error checking existing offer:', offerError);
        throw offerError;
      }

      if (existingOffer) {
        // 기존 오퍼 업데이트
        await supabase
          .from('product_offers')
          .update({
            discount_rate: product.discountRate,
            discounted_price: discountedPrice,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingOffer.id);
      } else {
        // 새 오퍼 생성
        await supabase
          .from('product_offers')
          .insert({
            product_id: product.productId,
            offer_type: 'RESERVATION_DISCOUNT',
            discount_rate: product.discountRate,
            discounted_price: discountedPrice,
            is_active: true,
            is_current: true
          });
      }

      // 2. product_stock에 내일 재고 설정
      await upsertProductStock(product.productId, tomorrowStr, product.stock);
    }

    return { success: true };
  } catch (error) {
    console.error('Set reservation discount products error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '예약할인 상품 설정에 실패했습니다'
    };
  }
}

/**
 * 상품을 등록합니다
 */
export async function createProducts(
  restaurantId: string,
  products: Array<{
    name: string;
    description?: string;
    category?: string;
    originalPrice: number;
    stock: number;
    imageUrl?: string;
  }>
): Promise<{ success: boolean; productIds?: string[]; error?: string }> {
  const productIds: string[] = [];
  
  try {
    const today = getKoreanDate();
    
    for (const product of products) {
      // 1. products 테이블에 상품 생성
      const { data: newProduct, error: productError } = await supabase
        .from('products')
        .insert({
          restaurant_id: restaurantId,
          name: product.name,
          description: product.description || null,
          category: product.category || null,
          original_price: product.originalPrice,
          stock_quantity: product.stock || 20,
          image_url: product.imageUrl,
          is_available: true
        })
        .select()
        .single();

      if (productError) throw productError;

      // 생성된 상품 ID를 순서대로 저장
      productIds.push(newProduct.id);

      // 2. product_stock에 당일 재고 생성
      await upsertProductStock(newProduct.id, today, product.stock);

      // 3. 일반주문 offer 자동 생성
      await supabase
        .from('product_offers')
        .insert({
          product_id: newProduct.id,
          offer_type: 'REGULAR_ORDER',
          discount_rate: 0,
          discounted_price: product.originalPrice,
          is_active: true,
          is_current: true
        });
    }

    return { success: true, productIds };
  } catch (error) {
    console.error('Create products error:', error);
    return {
      success: false,
      productIds: productIds.length > 0 ? productIds : undefined,
      error: error instanceof Error ? error.message : '상품 등록에 실패했습니다'
    };
  }
}

/**
 * 상품을 수정합니다
 */
export async function updateProduct(
  productId: string,
  updates: {
    name?: string;
    description?: string;
    category?: string;
    originalPrice?: number;
    stock?: number;
    imageUrl?: string;
  },
  options?: {
    imageFile?: File;
  }
): Promise<{ success: boolean; imageUrl?: string; error?: string }> {
  try {
    const today = getKoreanDate();
    let resolvedImageUrl = updates.imageUrl;

    if (options?.imageFile) {
      const uploadResult = await imageService.uploadProductImage(
        options.imageFile,
        productId
      );

      if (!uploadResult.success || !uploadResult.url) {
        return {
          success: false,
          error: uploadResult.error || '이미지 업로드에 실패했습니다.'
        };
      }

      resolvedImageUrl = uploadResult.url;
    }
    
    // 1. products 테이블 업데이트
    const updateData: any = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description || null;
    if (updates.category !== undefined) updateData.category = updates.category || null;
    if (updates.originalPrice !== undefined) updateData.original_price = updates.originalPrice;
    if (updates.stock !== undefined) updateData.stock_quantity = updates.stock;
    if (resolvedImageUrl !== undefined) updateData.image_url = resolvedImageUrl;
    
    // 재고가 0이면 is_available을 false로
    if (updates.stock === 0) {
      updateData.is_available = false;
    } else if (updates.stock && updates.stock > 0) {
      updateData.is_available = true;
    }

    await supabase
      .from('products')
      .update(updateData)
      .eq('id', productId);

    // 2. 재고 업데이트: 오늘자 initial_stock만 업데이트, current_stock는 보존 (레코드 없으면 생성)
    if (updates.stock !== undefined) {
      const { data: existingStock, error: stockError } = await supabase
        .from('product_stock')
        .select('id, initial_stock, current_stock, sold_stock')
        .eq('product_id', productId)
        .eq('date', today)
        .maybeSingle();

      if (stockError && stockError.code !== 'PGRST116') {
        throw stockError;
      }

      if (existingStock?.id) {
        // 판매 누적(sold_stock)과 current_stock 유지, initial만 덮어씀
        await supabase
          .from('product_stock')
          .update({
            initial_stock: updates.stock,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingStock.id);
      } else {
        // 오늘 레코드가 없으면 새로 생성 (판매 0 가정)
        await supabase
          .from('product_stock')
          .insert({
            product_id: productId,
            date: today,
            initial_stock: updates.stock,
            current_stock: updates.stock,
            sold_stock: 0
          });
      }
    }

    // 3. 가격 변경 시 일반주문 offer 업데이트
    if (updates.originalPrice !== undefined) {
      await supabase
        .from('product_offers')
        .update({
          discounted_price: updates.originalPrice,
          updated_at: new Date().toISOString()
        })
        .eq('product_id', productId)
        .eq('offer_type', 'REGULAR_ORDER')
        .eq('is_current', true);
    }

    return { success: true, imageUrl: resolvedImageUrl };
  } catch (error) {
    console.error('Update product error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '상품 수정에 실패했습니다'
    };
  }
}

/**
 * 상품을 삭제합니다
 */
export async function deleteProduct(productId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // CASCADE 설정으로 관련 offer, stock도 자동 삭제됨
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Delete product error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '상품 삭제에 실패했습니다'
    };
  }
}
